import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CuadernoCampoSetting {
  enabled?: boolean;
  /** `true` (el booleano, nada más): TODAS las empresas, también las futuras (05-10-2026). */
  all?: boolean;
  /** SOLO esas empresas. Vacía o ausente: ninguna (mismo contrato que `pest_foci`). */
  companies?: string[];
}

/**
 * ¿Está encendido el cuaderno de campo («Modelo de la Junta (v9)» y el modo
 * «@ Cuaderno de campo» del asistente) para ESTA empresa? (D24 del plan del
 * cuaderno: primero Ulture QA, luego la demo, y los clientes con orden de Pablo.)
 *
 * Mismo patrón que `fociEnabledFor`: sin fila, apagado; `enabled` a false,
 * apagado para todas; la lista vacía NO significa «todas» (quitar las dos
 * cuentas QA del piloto no puede encenderlo para todas las empresas). «Todas»
 * es explícito (petición de Pablo, 05-10-2026): `{"enabled": true, "all": true}`,
 * también para las empresas que se den de alta después. Solo el booleano `true`
 * cuenta, en `enabled` y en `all`.
 *
 * La fila se lee con la sesión: la política de lectura de `platform_settings` es
 * una LISTA de claves y `cuaderno_campo` entra con la migración 20260926160000
 * (B1 de la revisión de la ola 3; sin ella, «sin fila» = apagado para todos).
 *
 * La MISMA regla que la edge (`supabase/functions/ipm-ai-assistant/mode.ts#cuadernoEnabledFor`,
 * que la vuelve a mirar en cada turno): si cambia una, cambia la otra
 * (assistantModes.test.ts e interruptoresTodas.parity.test.ts las comparan).
 */
export function cuadernoCampoEnabledFor(setting: CuadernoCampoSetting | null | undefined, companyId: string | null | undefined): boolean {
  if (!setting || typeof setting !== "object" || !companyId) return false;
  const s = setting as { enabled?: unknown; all?: unknown; companies?: unknown };
  if (s.enabled !== true) return false;
  if (s.all === true) return true;
  return Array.isArray(s.companies) && s.companies.includes(companyId);
}

/** Solo admin y encargado ven el cuaderno (A8: el perito y el trabajador, no). */
export const ROLES_CUADERNO = ["admin", "manager"] as const;

/**
 * El interruptor para la interfaz. Mientras carga vale `false`: ante la duda no
 * se enseña un botón que después desaparece.
 *   - `enabled`: la empresa lo tiene encendido;
 *   - `allowed`: encendido Y quien mira es admin o encargado (A8). Es el que
 *     decide TODO lo que se enseña: el «@» y el «+» del chat, el botón de
 *     /spray-logs, la tarjeta de descarga y la de cambios.
 */
export function useCuadernoCampoEnabled() {
  const { company, role } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["cuaderno-campo-enabled"],
    queryFn: async (): Promise<CuadernoCampoSetting | null> => {
      const { data } = await supabase.from("platform_settings").select("value").eq("key", "cuaderno_campo").maybeSingle();
      return ((data as { value?: CuadernoCampoSetting } | null)?.value ?? null) as CuadernoCampoSetting | null;
    },
    staleTime: 5 * 60_000,
  });
  const enabled = cuadernoCampoEnabledFor(data, company?.id);
  const rolOk = (ROLES_CUADERNO as readonly string[]).includes(role ?? "");
  return { enabled, allowed: enabled && rolOk, loading: isLoading };
}
